import { type FieldValues, type Path, useFormContext, useWatch } from "react-hook-form";
import { DatePicker } from "@/components/DatePicker";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { FormField } from "@/components/ui/form";

type Props<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  label: string;
  required?: boolean;
  isModal?: boolean;
};

/**
 * DatePicker component that works correctly with React Hook Form default values.
 *
 * This component fixes the issue where clearing a date field with a default value
 * would not update the UI properly. It uses useWatch to get the current form value
 * instead of relying only on field.value, which doesn't always update immediately
 * when set to undefined.
 */
export function FormDatePicker<TFieldValues extends FieldValues>({
  name,
  label,
  required,
  isModal,
}: Props<TFieldValues>) {
  const { control } = useFormContext();
  const value = useWatch({ control, name });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItemWithMessage
          label={label}
          required={required}
          inputComponent={<DatePicker {...field} value={value} onChange={field.onChange} isModal={isModal} />}
        />
      )}
    />
  );
}
